import type { LucideIcon, LucideProps } from 'lucide-react';
import { type CSSProperties, forwardRef } from 'react';

import { useIsBrandedIcons } from '../../hooks/use-is-branded-icons';
import { BRANDED_GRID_SCALE, resolveBrandedStrokeWidth } from './branded-stroke';

// Kit frame — glyphs are 16-grid, round caps; generated data is `d`-only, so
// this lives here. Stroke weight is size-tuned in `./branded-stroke`.
const VIEW_BOX = '0 0 16 16';

// Mirrors of lucide's own helpers, so the branded <svg> keeps the two things a
// bare <svg> would drop: the `lucide lucide-<name>` classes (some e2e locators
// select `svg.lucide-*`) and `aria-hidden` when no a11y prop is passed.
export function mergeClasses(...classes: Array<string | undefined>): string {
  return classes
    .filter((c, i, arr) => Boolean(c) && c?.trim() !== '' && arr.indexOf(c) === i)
    .join(' ')
    .trim();
}

export function hasA11yProp(props: Record<string, unknown>): boolean {
  return Object.keys(props).some(
    (key) => key.startsWith('aria-') || key === 'role' || key === 'title'
  );
}

// lucide's own kebab/pascal helpers. It emits BOTH `lucide-<kebab(pascal(name))>`
// and `lucide-<name>`, which differ for names with a trailing number
// (`trash-2` -> `lucide-trash2` + `lucide-trash-2`). Reproduce both so a class
// selector targeting either form still matches the branded glyph.
function toPascalCase(input: string): string {
  return input.replace(/(^|-)([a-z0-9])/g, (_match, _sep, char: string) =>
    char.toUpperCase()
  );
}

function toKebabCase(input: string): string {
  // Exact mirror of lucide's toKebabCase — no acronym rule (adding one flips
  // `file-x-corner` to `file-xcorner`, diverging from lucide's class).
  return input.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Builds one icon's branded replacement. When the flag is on it renders the
 * branded glyph; when off it renders `Original` (the real lucide icon this
 * replaces) untouched. The next.config plugin swaps lucide's per-icon module
 * for a generated `branded/<name>.tsx` that calls this.
 *
 * Stroke-based like lucide: `size`/`color`/`className` pass through. A call-site
 * `strokeWidth` is lucide-tuned for a 24-grid, so it's scaled to the 16-grid to
 * render the same weight it would on lucide (otherwise a passed `3` would come
 * out 1.5x thicker). With no override the stroke is size-tuned (`strokeForSize`)
 * when `size` is passed, else a 16px-tuned fallback (a CSS-`size-N` icon doesn't
 * reach this component). The `--branded-grid-scale` var lets the DS's 24-grid
 * `stroke-icon-*` CSS land at the right weight when it overrides us in a Button.
 * `absoluteStrokeWidth` keeps the stroke a fixed pixel width regardless of size.
 */
export function makeBrandedIcon(
  name: string,
  Original: LucideIcon,
  paths: string[]
): LucideIcon {
  const nameClasses = mergeClasses(
    'lucide',
    `lucide-${toKebabCase(toPascalCase(name))}`,
    `lucide-${name}`
  );

  const BrandedIcon = forwardRef<SVGSVGElement, LucideProps>(
    function BrandedIcon(props, ref) {
      const branded = useIsBrandedIcons();

      if (!branded) {
        return <Original {...props} ref={ref} />;
      }

      const {
        size,
        strokeWidth,
        absoluteStrokeWidth,
        color,
        className,
        style,
        children,
        ...rest
      } = props;

      const resolvedStrokeWidth = resolveBrandedStrokeWidth({
        size,
        strokeWidth,
        absoluteStrokeWidth,
      });

      const styleWithGridScale = {
        ...style,
        '--branded-grid-scale': BRANDED_GRID_SCALE,
      } as CSSProperties;

      const a11yHidden =
        !children && !hasA11yProp(rest) ? { 'aria-hidden': 'true' as const } : undefined;

      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size ?? 24}
          height={size ?? 24}
          viewBox={VIEW_BOX}
          fill="none"
          stroke={color ?? 'currentColor'}
          strokeWidth={resolvedStrokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={mergeClasses(nameClasses, className)}
          style={styleWithGridScale}
          {...a11yHidden}
          ref={ref}
          {...rest}
        >
          {paths.map((d, index) => (
            <path key={index} d={d} />
          ))}
          {children}
        </svg>
      );
    }
  );
  BrandedIcon.displayName = `Branded(${name})`;
  return BrandedIcon;
}
